/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) 2021 Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { observer } from 'mobx-react';
import React, { ReactElement } from 'react';
import { useViewModel } from '../../../../../hooks/useViewModel';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Grid } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import CancelIcon from '@material-ui/icons/Cancel';
import AddModuleDialogViewStyles from './AddModuleDialogViewStyles';
import { ArrowDropDown, ArrowRight, Done } from '@material-ui/icons';
import { AddModuleDialogViewModel } from './AddModuleDialogViewModel';
import { ModulePackage } from '../../../../../types/course/ModulePackage';

const AddModuleDialog = observer((): ReactElement => {
	const classes = AddModuleDialogViewStyles();
	const viewModel: AddModuleDialogViewModel = useViewModel(AddModuleDialogViewModel);

	return (
		<Dialog
			open={viewModel.isAddModuleDialogOpen()}
			onClose={() => viewModel.closeAddModuleDialog()}
			fullWidth
			maxWidth={'md'}
			aria-labelledby='add-module-title'
			aria-describedby='add-module-description'>
			<DialogTitle disableTypography className={classes.dialogTitle} id='add-module-title'>
				<FormattedMessage id='modules.add_modules_dialog.add_modules' />
				<CancelIcon
					className={classes.closeButton}
					fontSize={'small'}
					onClick={() => viewModel.closeAddModuleDialog()}
				/>
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				<Grid container className={`${classes.row} ${classes.headline}`}>
					<Grid item xs={9}>
						<Grid container>
							<Grid item xs={1} />
							<Grid item xs={5}>
								<FormattedMessage id='modules.add_modules_dialog.module_name' />
							</Grid>
							<Grid item xs={5}>
								<FormattedMessage id='modules.add_modules_dialog.module_description' />
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={3} />
				</Grid>

				{viewModel.isLoading() ? (
					<Box className={classes.loadingContainer}>
						<CircularProgress className={classes.loading} />
					</Box>
				) : (
					viewModel.getAllModulePackages().map((modulePackage: ModulePackage) => {
						return (
							<Grid container key={modulePackage.id} className={`${classes.row} ${classes.content}`}>
								<Grid
									item
									xs={9}
									onClick={() =>
										viewModel.isCurrentModulePackage(modulePackage.id)
											? viewModel.setOpenModulePackageId(undefined)
											: viewModel.setOpenModulePackageId(modulePackage.id)
									}>
									<Grid container alignItems={'flex-start'} className={classes.contentContainer}>
										<Grid item xs={1} className={classes.flex}>
											{viewModel.isCurrentModulePackage(modulePackage.id) ? <ArrowDropDown /> : <ArrowRight />}
										</Grid>
										<Grid item xs={5}>
											{modulePackage.name}
										</Grid>
										<Grid item xs={5}>
											<Box className={viewModel.isCurrentModulePackage(modulePackage.id) ? '' : classes.truncated}>
												{modulePackage.description}
											</Box>
										</Grid>
									</Grid>
								</Grid>
								<Grid item xs={3} className={classes.addButton}>
									{viewModel.isModulePackageInCourse(modulePackage.id) ? (
										<Button variant='outlined' disabled={true}>
											<Done />
											<FormattedMessage id='modules.add_modules_dialog.already_added' />
										</Button>
									) : (
										<Button variant='outlined' onClick={() => viewModel.addModulePackage(modulePackage.id)}>
											<FormattedMessage id='modules.add_modules_dialog.add_to_course' />
										</Button>
									)}
								</Grid>
							</Grid>
						);
					})
				)}
			</DialogContent>
		</Dialog>
	);
});

export default AddModuleDialog;
